import { AsyncPipe, DOCUMENT } from '@angular/common';
import {
    AfterViewInit,
    Component,
    computed,
    effect,
    HostListener,
    Inject,
    OnDestroy,
    OnInit,
    Signal,
} from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { RouterLink } from '@angular/router';
import { Store } from '@ngrx/store';
import { ConfigService } from '@polaris-customerweb/sharedcomponents.config';
import {
    NotificationMessage,
    NotificationMessageService,
    VehicleService,
} from '@polaris-customerweb/sharedcomponents.garage';
import {
    ConsoleLogService,
    RoutingHelpersService,
} from '@polaris-customerweb/sharedcomponents.shared';
import {
    ConnectedVehicleService,
    EvSocAlertComponent,
    VehicleDataSubscriptionService,
} from '@polaris-customerweb/sharedcomponents.vehicle-health';
import { AngularSvgIconModule } from 'angular-svg-icon';
import { defer } from 'lodash-es';
import { combineLatest, Observable } from 'rxjs';
import {
    filter,
    map,
    switchMap,
    takeWhile,
    tap,
    withLatestFrom,
} from 'rxjs/operators';
import { AnalyticsDataDirective } from 'src/app/directives/analytics-data.directive';
import { AnalyticsDirective } from 'src/app/directives/analytics.directive';
import { AppState } from 'src/app/models/app-state';
import { DateTimePipe } from 'src/app/pipes/date-time.pipe';
import { StringPipe } from 'src/app/pipes/string.pipe';
import { NotificationNavigationService } from 'src/app/services/notification-navigation.service';
import { WINDOW } from 'src/app/state/app-config/factories/window.factory';
import { selectGarageItems } from 'src/app/state/garage/garage.selectors';
import { selectActiveVehicleId } from 'src/app/state/vehicle';

@Component({
    imports: [
        AnalyticsDataDirective,
        AnalyticsDirective,
        AngularSvgIconModule,
        AsyncPipe,
        DateTimePipe,
        StringPipe,
        RouterLink,
    ],
    selector: 'rdx-notification',
    standalone: true,
    styleUrls: ['./notification.component.scss'],
    templateUrl: './notification.component.html',
})
export class NotificationComponent implements OnInit, OnDestroy, AfterViewInit {
    readonly className = 'NotificationComponent';
    $notifications: Signal<NotificationMessage[]>;
    $newNotifications: Signal<NotificationMessage[]>;
    showingNotificationPanel = false;
    isEpi: boolean;
    notificationPreferencesLink$: Observable<string>;
    hasShownEvDialog = false;

    private isDestroyed = false;

    constructor(
        private configService: ConfigService,
        private connectedVehicleService: ConnectedVehicleService,
        private consoleLogService: ConsoleLogService,
        @Inject(DOCUMENT) private document: Document,
        private matDialog: MatDialog,
        private notificationMessageService: NotificationMessageService,
        private notificationNavigationService: NotificationNavigationService,
        private routingHelpersService: RoutingHelpersService,
        private store: Store<AppState>,
        private vehicleDataSubscriptionService: VehicleDataSubscriptionService,
        private vehicleService: VehicleService,
        @Inject(WINDOW) private window: Window
    ) {
        notificationMessageService
            .pollMessages$()
            .pipe(takeWhile(() => !this.isDestroyed))
            .subscribe();
        this.isEpi = this.configService.isEpi;
        this.$notifications = computed(() =>
            this.notificationMessageService
                .$notifications()
                .filter((notification) =>
                    ['New', 'Read'].includes(notification.notificationStatus)
                )
        );
        this.$newNotifications = computed(() =>
            this.$notifications().filter(
                (notification) => notification.notificationStatus === 'New'
            )
        );
        effect(() => {
            if (
                this.$newNotifications().some(
                    (notification) =>
                        notification.notificationType === 'GeofenceBreach' ||
                        notification.notificationType === 'BumpAlert'
                )
            ) {
                this.connectedVehicleService.triggerImmediateActiveAlertPoll();
            }
        });
        effect(() => {
            const alert = notificationMessageService.$socAlert();
            if (alert.vin && !this.hasShownEvDialog) {
                this.hasShownEvDialog = true;
                this.matDialog
                    .open<EvSocAlertComponent>(EvSocAlertComponent, {
                        data: alert,
                    })
                    .afterClosed()
                    .subscribe(() => {
                        notificationMessageService.dismissNotification(
                            alert.id
                        );
                    });
            }
        });
    }

    @HostListener('window:resize', [])
    onWindowResize() {
        this.addEpiBell();
    }

    ngOnInit(): void {
        this.notificationPreferencesLink$ = this.store
            .select(selectActiveVehicleId)
            .pipe(
                withLatestFrom(this.store.select(selectGarageItems)),
                filter(([vehicleId, items]) =>
                    Boolean(vehicleId || items[0]?.id)
                ),
                map(([vehicleId, items]) =>
                    this.routingHelpersService.combineUrl(
                        '/maintenance/vehicle',
                        vehicleId ||
                            items.find((x) =>
                                x.isOnBrand?.(this.configService.appSku)
                            )?.id ||
                            items[0].id,
                        'notification-preferences/'
                    )
                ),
                takeWhile(() => !this.isDestroyed)
            );
    }

    ngAfterViewInit(): void {
        this.addEpiBell();
        this.getVehicleData();
    }

    ngOnDestroy(): void {
        this.isDestroyed = true;
    }

    getVehicleData() {
        defer(() => {
            this.vehicleService
                .getVehicleVins$()
                .pipe(
                    switchMap((vins) =>
                        combineLatest(
                            vins.map((x) =>
                                this.vehicleDataSubscriptionService
                                    .subscribeToVehicleData(x)
                                    .pipe(
                                        tap((vehicleState) => {
                                            if (this.configService.isDebug) {
                                                for (const message of vehicleState.getDebugInfo()) {
                                                    this.consoleLogService.debug(
                                                        message
                                                    );
                                                }
                                            }
                                        })
                                    )
                            )
                        ).pipe(takeWhile(() => !this.isDestroyed))
                    )
                )
                .subscribe();
        });
    }

    showDetails(message: NotificationMessage): void {
        this.notificationMessageService.readNotification(message.id);
        this.notificationNavigationService.navigateToNotification(message);
        this.showingNotificationPanel = false;
    }

    markNotificationDismissed(message: NotificationMessage): void {
        this.notificationMessageService.dismissNotification(message.id);
    }

    markAllNotificationsDismissed(): void {
        this.notificationMessageService.dismissAllNotifications();
    }

    markAllNotificationsRead(): void {
        this.notificationMessageService.readAllNotifications();
    }

    toggleMenu(): void {
        if (this.showingNotificationPanel) {
            this.markAllNotificationsRead();
        }

        this.showingNotificationPanel = !this.showingNotificationPanel;
    }

    addEpiBell() {
        if (this.isEpi) {
            const mediaQuery = this.window.matchMedia('(max-width: 1200px)');

            const mainHeader = this.document.querySelector(
                '.secondary-nav-container > ul'
            );

            const epiHeader = this.document.querySelector(
                '.header-simple__secondary'
            );
            const notificationBell =
                this.document.querySelector('.notification-bell');

            if (epiHeader && notificationBell && mediaQuery.matches) {
                notificationBell.classList.add('cms-ride-command');
                epiHeader.prepend(notificationBell);
            } else if (mainHeader && notificationBell) {
                mainHeader.prepend(notificationBell);
            }
        }
    }
}
